import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Employee, UnavailableTime } from '../types';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { HistoryService } from '../services/historyService';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  onSnapshot,
  query,
  orderBy,
  where
} from 'firebase/firestore';

interface EmployeeContextType {
  employees: Employee[];
  addEmployee: (employee: Omit<Employee, 'id'>) => void;
  updateEmployee: (id: string, updates: Partial<Employee>) => void;
  deleteEmployee: (id: string) => void;
  getEmployee: (id: string) => Employee | undefined;
  resetToMockEmployees: () => void;
  getEmployeesByStore: (storeId: string) => Employee[];
  getAllEmployees: () => Employee[];
  isLoading: boolean;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

// Colores predefinidos para empleados
const defaultColors = [
  '#3B82F6', // Azul
  '#10B981', // Verde
  '#F59E0B', // Amarillo
  '#EF4444', // Rojo
  '#8B5CF6', // Púrpura
  '#06B6D4', // Cian
  '#F97316', // Naranja
  '#84CC16', // Lima
  '#EC4899', // Rosa
  '#6B7280'  // Gris
];

// Función para obtener el siguiente color disponible
const getNextAvailableColor = (existingEmployees: Employee[]): string => {
  const usedColors = existingEmployees.map(emp => emp.color);
  const availableColor = defaultColors.find(color => !usedColors.includes(color));
  return availableColor || defaultColors[Math.floor(Math.random() * defaultColors.length)];
};

// Función para generar un PIN único de 5 dígitos
const generateUniquePin = (existingEmployees: Employee[]): string => {
  const usedPins = existingEmployees.map(emp => emp.pin);
  let newPin: string;
  do {
    newPin = Math.floor(10000 + Math.random() * 90000).toString(); // 10000-99999
  } while (usedPins.includes(newPin));
  return newPin;
};

// Empleados de prueba
const mockEmployees: Employee[] = [
  {
    id: '1',
    name: 'Ana Perez',
    username: 'ana.perez',
    password: '12345',
    weeklyLimit: 40,
    unavailableTimes: [
      {
        id: '1',
        dayOfWeek: 2, // Martes
        startTime: '14:00',
        endTime: '18:00'
      }
    ],
    birthday: '10/04/1995',
    isActive: true,
    color: '#3B82F6', // Azul
    role: 'encargado'
  },
  {
    id: '2',
    name: 'Luis Gomez',
    username: 'luis.gomez',
    password: '23456',
    weeklyLimit: 35,
    unavailableTimes: [],
    birthday: '15/08/1990',
    isActive: true,
    color: '#10B981', // Verde
    role: 'empleado'
  },
  {
    id: '3',
    name: 'María Rodriguez',
    username: 'maria.rodriguez',
    password: '34567',
    weeklyLimit: 30,
    unavailableTimes: [
      {
        id: '2',
        dayOfWeek: 0, // Domingo
        startTime: '09:00',
        endTime: '12:00'
      }
    ],
    birthday: '22/03/1988',
    isActive: true,
    color: '#F59E0B', // Amarillo
    role: 'empleado'
  },
  {
    id: '4',
    name: 'Carlos Martinez',
    username: 'carlos.martinez',
    password: '45678',
    weeklyLimit: 25,
    unavailableTimes: [],
    birthday: '05/12/1992',
    isActive: true,
    color: '#EF4444', // Rojo
    role: 'empleado'
  },
  {
    id: '5',
    name: 'Sofia Lopez',
    weeklyLimit: 32,
    unavailableTimes: [
      {
        id: '3',
        dayOfWeek: 6, // Sábado
        startTime: '18:00',
        endTime: '22:00'
      }
    ],
    birthday: '18/07/1996',
    isActive: true,
    color: '#8B5CF6', // Púrpura
    username: 'sofia.lopez',
    password: '56789',
    role: 'empleado'
  },
  {
    id: '6',
    name: 'Diego Fernandez',
    username: 'diego.fernandez',
    password: '67890',
    weeklyLimit: 28,
    unavailableTimes: [],
    birthday: '03/11/1991',
    isActive: true,
    color: '#06B6D4', // Cian
    role: 'empleado'
  },
  {
    id: '7',
    name: 'Valentina Torres',
    weeklyLimit: 26,
    unavailableTimes: [
      {
        id: '4',
        dayOfWeek: 1, // Lunes
        startTime: '08:00',
        endTime: '10:00'
      }
    ],
    birthday: '14/09/1994',
    isActive: true,
    color: '#F97316', // Naranja
    username: 'valentina.torres',
    password: '78901',
    role: 'empleado'
  },
  {
    id: '8',
    name: 'Roberto Silva',
    username: 'roberto.silva',
    password: '89012',
    weeklyLimit: 30,
    unavailableTimes: [],
    birthday: '28/01/1989',
    isActive: true,
    color: '#84CC16', // Lima
    role: 'empleado'
  },
  {
    id: '9',
    name: 'Camila Herrera',
    username: 'camila.herrera',
    password: '90123',
    weeklyLimit: 24,
    unavailableTimes: [
      {
        id: '5',
        dayOfWeek: 3, // Miércoles
        startTime: '16:00',
        endTime: '20:00'
      }
    ],
    birthday: '12/06/1993',
    isActive: true,
    color: '#EC4899', // Rosa
    role: 'empleado'
  },
  {
    id: '10',
    name: 'Andres Morales',
    username: 'andres.morales',
    password: '01234',
    weeklyLimit: 35,
    unavailableTimes: [],
    birthday: '07/02/1990',
    isActive: true,
    color: '#6B7280', // Gris
    role: 'empleado'
  }
];

export function EmployeeProvider({ children }: { children: ReactNode }) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { currentStore } = useStore();
  const { currentEmployee } = useAuth();

  // Cargar todos los empleados para estadísticas globales
  useEffect(() => {
    const employeesRef = collection(db, 'employees');
    const q = query(employeesRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allEmployeesData: Employee[] = [];
      
      snapshot.forEach((doc) => {
        const employeeData = { id: doc.id, ...doc.data() } as Employee;
        allEmployeesData.push(employeeData);
      });
      
      // Ordenar por nombre en JavaScript
      allEmployeesData.sort((a, b) => a.name.localeCompare(b.name));
      
      setAllEmployees(allEmployeesData);
    }, (error) => {
      console.error('Error loading all employees:', error);
    });

    return () => unsubscribe();
  }, []);

  // Cargar empleados desde Firebase filtrados por tienda
  useEffect(() => {
    if (!currentStore) {
      setEmployees([]);
      setIsLoading(false);
      return;
    }

    // Filtrar empleados de la tienda actual desde allEmployees
    const storeEmployees = allEmployees.filter(emp => emp.storeId === currentStore.id);
    setEmployees(storeEmployees);
    setIsLoading(false);
  }, [currentStore, allEmployees]);

  const addEmployee = async (employeeData: Omit<Employee, 'id'>) => {
    if (!currentStore) {
      throw new Error('No hay tienda seleccionada');
    }

    try {
      const newEmployee: Omit<Employee, 'id'> = {
        ...employeeData,
        storeId: currentStore.id,
        color: employeeData.color || getNextAvailableColor(employees),
        role: employeeData.role || 'empleado'
      };

      const docRef = await addDoc(collection(db, 'employees'), newEmployee);
      
      // Registrar en historial
      if (currentEmployee) {
        await HistoryService.logEmployeeCreated(docRef.id, currentEmployee.id, employeeData.name);
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      throw error;
    }
  };

  const updateEmployee = async (id: string, updates: Partial<Employee>) => {
    try {
      const employee = allEmployees.find(emp => emp.id === id);
      if (!employee) {
        throw new Error('Empleado no encontrado');
      }

      // Preparar cambios para el historial
      const changes: Record<string, { old: any; new: any }> = {};
      Object.keys(updates).forEach(key => {
        if (key !== 'updatedAt' && key !== 'createdAt' && key !== 'id') {
          changes[key] = {
            old: employee[key as keyof Employee],
            new: updates[key as keyof Employee]
          };
        }
      });

      const employeeRef = doc(db, 'employees', id);
      await updateDoc(employeeRef, updates);
      
      // Registrar en historial
      if (currentEmployee && Object.keys(changes).length > 0) {
        await HistoryService.logEmployeeUpdated(id, currentEmployee.id, changes, employee.name);
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const deleteEmployee = async (id: string) => {
    try {
      const employee = allEmployees.find(emp => emp.id === id);
      await deleteDoc(doc(db, 'employees', id));
      
      // Registrar en historial
      if (currentEmployee && employee) {
        await HistoryService.logEmployeeDeleted(id, currentEmployee.id, employee.name);
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  };

  const getEmployee = (id: string): Employee | undefined => {
    return employees.find(emp => emp.id === id);
  };

  const getEmployeesByStore = (storeId: string): Employee[] => {
    return allEmployees.filter(emp => emp.storeId === storeId);
  };

  const getAllEmployees = (): Employee[] => {
    return allEmployees;
  };

  const resetToMockEmployees = async () => {
    if (!currentStore) {
      throw new Error('No hay tienda seleccionada');
    }

    try {
      setIsLoading(true);
      
      // Eliminar solo los empleados de la tienda actual
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('storeId', '==', currentStore.id));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Agregar empleados de prueba con el storeId correcto
      const addPromises = mockEmployees.map(employee => {
        const { id, ...employeeData } = employee;
        return addDoc(employeesRef, { ...employeeData, storeId: currentStore.id });
      });
      
      await Promise.all(addPromises);
    } catch (error) {
      console.error('Error resetting to mock employees:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EmployeeContext.Provider value={{
      employees,
      addEmployee,
      updateEmployee,
      deleteEmployee,
      getEmployee,
      resetToMockEmployees,
      getEmployeesByStore,
      getAllEmployees,
      isLoading
    }}>
      {children}
    </EmployeeContext.Provider>
  );
}

export function useEmployees() {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
}